import React, { useState, useEffect, useCallback } from 'react';
import { ruleLibraryService } from '../../../config/api';

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
);

const RuleLibraryBrowser = ({ onSelect, onCancel }) => {
    const [templates, setTemplates] = useState([]);
    const [userRules, setUserRules] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [activeTab, setActiveTab] = useState('SYSTEM'); // 'SYSTEM' or 'USER'

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        // Only trigger server-side search for SYSTEM templates
        if (activeTab === 'SYSTEM') {
            const timer = setTimeout(() => {
                handleSearch();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [searchQuery, selectedCategory, activeTab]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [userRulesRes, categoriesRes] = await Promise.all([
                ruleLibraryService.listUserRules(),
                ruleLibraryService.getCategories()
            ]);
            setUserRules(userRulesRes.data || []);
            setCategories(categoriesRes.data || []);

            // Initial load of templates
            await handleSearch();
        } catch (error) {
            console.error('Error fetching rule library data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = useCallback(async () => {
        try {
            // If search query is empty and category is ALL, we can just list templates
            // Otherwise use search API
            let response;
            if (!searchQuery && selectedCategory === 'ALL') {
                response = await ruleLibraryService.listTemplates();
            } else {
                const categoryParam = selectedCategory === 'ALL' ? null : selectedCategory;
                response = await ruleLibraryService.searchTemplates(searchQuery, categoryParam);
            }
            setTemplates(response.data || []);
        } catch (error) {
            console.error('Error searching templates:', error);
        }
    }, [searchQuery, selectedCategory]);

    const filteredItems = activeTab === 'SYSTEM'
        ? templates // Already filtered by server
        : userRules.filter(item => {
            const query = searchQuery.toLowerCase();
            const matchesSearch = item.name.toLowerCase().includes(query) ||
                (item.description && item.description.toLowerCase().includes(query));
            const matchesCategory = selectedCategory === 'ALL' || item.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

    const getComplexityBadge = (complexity) => {
        switch (complexity) {
            case 'BASIC': return 'badge-success';
            case 'INTERMEDIATE': return 'badge-warning';
            case 'ADVANCED': return 'badge-error';
            default: return 'badge-ghost';
        }
    };

    return (
        <div className="flex flex-col h-full max-h-[600px]">
            <div className="flex flex-col sm:flex-row gap-4 mb-6 sticky top-0 bg-base-100 z-10 py-2">
                {/* Search */}
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        className="input input-bordered w-full pl-10"
                        placeholder="Search rules..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Category Filter */}
                <div className="flex items-center gap-2">
                    <FilterIcon />
                    <select
                        className="select select-bordered"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <option value="ALL">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.name} value={cat.name}>{cat.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered mb-4">
                <a
                    role="tab"
                    className={`tab ${activeTab === 'SYSTEM' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('SYSTEM')}
                >
                    System Templates
                </a>
                <a
                    role="tab"
                    className={`tab ${activeTab === 'USER' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('USER')}
                >
                    My Saved Rules
                </a>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="text-center py-12 bg-base-200 rounded-xl">
                        <p className="text-base-content/60">No rules found matching your criteria</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredItems.map(item => (
                            <div
                                key={item.id}
                                className="card bg-base-100 border border-base-300 hover:border-primary cursor-pointer transition-all hover:shadow-md"
                                onClick={() => onSelect(item)}
                            >
                                <div className="card-body p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-base">{item.name}</h3>
                                        <div className={`badge badge-sm ${getComplexityBadge(item.complexity)}`}>
                                            {item.complexity}
                                        </div>
                                    </div>
                                    <p className="text-xs text-base-content/70 line-clamp-2 mb-3">
                                        {item.description || 'No description available'}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-auto">
                                        {item.category && (
                                            <div className="badge badge-outline badge-xs opacity-60">
                                                {item.category}
                                            </div>
                                        )}
                                        {item.tags && item.tags.slice(0, 3).map(tag => (
                                            <div key={tag} className="badge badge-ghost badge-xs opacity-60">
                                                #{tag}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="modal-action mt-6">
                <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

export default RuleLibraryBrowser;
